import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Pen } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api/client';
import { useAuth } from '@/features/auth';
import { createTaskSchema, type TaskFormValues } from '../schemas';
import { useNewTask, useNewTaskMutations } from '../hooks';
import type { CreateTaskDto } from '../types';
import { TaskDialogForm } from './TaskDialogForm';

interface MatterData {
  id: number;
  employees?: Array<{ id: number; name: string }>;
}

interface TaskDialogProps {
  taskId?: number | undefined;
  defaultMatterId?: number | undefined;
  onSuccess?: (() => void) | undefined;
  trigger?: React.ReactNode | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
}

export function TaskDialog({
  taskId,
  defaultMatterId,
  onSuccess,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: TaskDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  const [matter, setMatter] = React.useState<MatterData | null>(null);
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = user?.role?.type === 'STAFF';
  const isEditMode = !!taskId;

  const { data: existingTask } = useNewTask(taskId);
  const { createMutation, updateMutation } = useNewTaskMutations();

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(createTaskSchema(isStaff)),
    defaultValues: {
      description: '',
      // Default due date to today (create mode). The existingTask effect below
      // overwrites this with the saved value in edit mode.
      dueAt: new Date(),
      typeId: undefined,
      matterId: defaultMatterId as number | undefined as number,
      // Default the assignee to the current user (create mode). In edit mode,
      // the existingTask effect below overwrites this with the saved value.
      assignedStaffId: user?.id,
      clientId: undefined,
    },
  });

  React.useEffect(() => {
    if (existingTask && open) {
      form.reset({
        description: existingTask.description ?? '',
        dueAt: existingTask.dueAt ? new Date(existingTask.dueAt) : undefined,
        typeId: existingTask.typeId ?? undefined,
        matterId: existingTask.matterId as number | undefined as number,
        assignedStaffId: existingTask.assignedStaffId ?? undefined,
        clientId: undefined,
      });
      if (existingTask.matterId) {
        loadMatterData(existingTask.matterId);
      }
    }
  }, [existingTask, open, form]);

  React.useEffect(() => {
    if (open && !isEditMode && defaultMatterId) {
      form.setValue('matterId', defaultMatterId);
      loadMatterData(defaultMatterId);
    }
  }, [open, isEditMode, defaultMatterId, form]);

  React.useEffect(() => {
    if (!open) {
      form.reset();
      setMatter(null);
    }
  }, [open, form]);

  const loadMatterData = async (matterId: number) => {
    try {
      const data = await api.get<MatterData>(`/matters/${matterId}`);
      setMatter(data);
    } catch {
      setMatter(null);
    }
  };

  const handleClientSelect = (item: Record<string, unknown> | null) => {
    const clientId = item?.['id'] as number | undefined;
    form.setValue('clientId', clientId);
    form.setValue('matterId', undefined as unknown as number);
    // Reset the assignee to the current user (the creator) — the default. The
    // staff field is hidden until a matter is picked, then handleMatterSelect
    // re-applies the same default.
    form.setValue('assignedStaffId', user?.id);
    setMatter(null);
  };

  const handleMatterSelect = async (item: Record<string, unknown> | null) => {
    const matterId = item?.['id'] as number | undefined;
    form.setValue('matterId', matterId as number | undefined as number, { shouldValidate: true });
    // Default the assignee to the current user (the creator) on matter change.
    // Changing matters resets any previously picked staff (who may not belong to
    // the new matter); self is the sensible default and a valid member in the
    // normal case. The user can still pick a different teammate.
    form.setValue('assignedStaffId', user?.id);
    if (matterId) {
      await loadMatterData(matterId);
    } else {
      setMatter(null);
    }
  };

  const onSubmit = async (values: TaskFormValues) => {
    const payload: CreateTaskDto = {
      description: values.description,
      dueAt: values.dueAt?.toISOString(),
      typeId: values.typeId,
      matterId: values.matterId,
      assignedStaffId: isStaff ? user?.id : values.assignedStaffId,
    };

    if (isEditMode && taskId) {
      await updateMutation.mutateAsync({ id: taskId, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    form.reset();
    setOpen(false);
    onSuccess?.();
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const getUniqueStaff = () => {
    const base = matter?.employees ?? [];
    // MD users may assign a task to THEMSELVES on any matter, even one they are
    // not a member of — so ensure the current MD appears in the assignee list.
    if (
      user?.role?.type === 'MD' &&
      user.id != null &&
      !base.some((s) => s.id === user.id)
    ) {
      return [...base, { id: user.id, name: user.name ?? t('me') }];
    }
    return base;
  };

  const uniqueStaff = getUniqueStaff();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger ?? (
            isEditMode ? (
              <Button size="sm" variant="outline">
                <Pen className="h-4 w-4" />
              </Button>
            ) : (
              <Button>{t('newTask')}</Button>
            )
          )}
        </DialogTrigger>
      )}

      <DialogContent className="!flex !flex-col sm:max-w-lg p-0 gap-0 max-h-[90vh]">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {isEditMode ? `${t('update')} ${t('task')}` : t('newTask')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditMode ? `${t('update')} ${t('task')}` : t('newTask')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <TaskDialogForm
            form={form}
            defaultMatterId={defaultMatterId}
            uniqueStaff={uniqueStaff}
            onClientSelect={handleClientSelect}
            onMatterSelect={handleMatterSelect}
            onSubmit={onSubmit}
          />
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('cancel')}
            </Button>
          </DialogClose>
          <Button type="submit" form="new-task-dialog-form" disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
